import { classNames } from "@ember-decorators/component"
import Component from "@ember/component"
import { action, get, set } from "@ember/object"
import { later } from "@ember/runloop"
import { inject as service } from "@ember/service"
import type {
  Card,
  CardGoogleClassroom,
  CardStandards,
  CardTitleValueHtml,
  CardValueHtml,
  LegacyCardStackUsedAsTypes,
} from "@shared/models/card-stack"
import type { Permission } from "@shared/models/permissions"
import config from "cc-frontend/config/environment"
import type { AiValueEditor } from "cc-frontend/lib/ai/ai-value-editor"
import type EmberStore from "cc-frontend/services/ember-store"
import type LessonEditor from "cc-frontend/services/lesson-editor"
import type { ModelsNeededForFirstEdit } from "cc-frontend/services/lesson-editor"
import type ModalManager from "cc-frontend/services/modal-manager"
import type Session from "cc-frontend/services/session"
import SortableNode from "ember-sortable/mixins/sortable-node"
import { completeSorting, hideSectionValues, startSorting } from "../../../../lib/section-moving-v5"
import styles from "./card.module.css"

interface Signature {
  Args: {
    model: CardTitleValueHtml | CardValueHtml | CardStandards | CardGoogleClassroom
    usedAs: LegacyCardStackUsedAsTypes
    permissions: Array<Permission>
    cardStackId: string
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit
    registerCardEditor?: (cardId: string, prop: string, editor: AiValueEditor<unknown>) => void
    openGoogleClassroomPostSidebar: (card: Card) => void
    cardIndex: number
    courseStandardSetIds?: Array<string>
    planbookRecentStandardSetIds?: Array<string>
    courseId: string
    planbookId: string
    hideMoveButton: boolean
  }
}

//@ts-ignore
@classNames("lesson-section-sortable")
//@ts-ignore
export default class CardComponent extends Component.extend(SortableNode)<Signature> {
  // uncomment this line to enable typing (and comment the line above). When we stop using this mixin, this problem will go away
  // export default class CardComponent extends Component<Signature> {
  @service("ember-store") declare store: EmberStore
  @service declare session: Session
  @service declare lessonEditor: LessonEditor
  @service declare modalManager: ModalManager
  @service dragging

  readonly styles = styles
  hideSectionValues = hideSectionValues.bind(this)
  startSorting = startSorting.bind(this)
  completeSorting = completeSorting.bind(this)

  handle = ".js-move-button"
  updateSectionTimer = null
  hoveringTimer: null | number = null
  state = {
    hasEntered: config.environment === "test" || false,
    isHovering: config.environment === "test" || false,
    allowSectionActionAnimation: false,
  }

  canReceiveSortable(_node) {
    return false
  }
  @action onMouseEnter() {
    if (this.hoveringTimer) clearTimeout(this.hoveringTimer)
    // @ts-ignore Dragging service isn't typed yet
    if (get(this, "dragging.isDragging")) return

    // We wait to display the actions until hover we're not rendering
    // more components than we need to.
    // @ts-ignore
    if (get(this, "state.hasEntered") === false) set(this, "state.hasEntered", true)
    // @ts-ignore
    if (get(this, "state.isHovering") === false) set(this, "state.isHovering", true)

    later(() => {
      // @ts-ignore This is because our typing for the component doesn't work with the mixin
      if (this.isDestroyed || this.isDestroying) return
      // @ts-ignore
      set(this, "state.allowSectionActionAnimation", true)
    }, 30)
  }

  @action onMouseLeave() {
    if (this.hoveringTimer) clearTimeout(this.hoveringTimer)
    // @ts-ignore This is because our typing for the component doesn't work with the mixin
    if (this.isDestroyed || this.isDestroying) return
    // @ts-ignore
    set(this, "state.isHovering", false)
    // @ts-ignore
    set(this, "state.allowSectionActionAnimation", false)
  }

  @action onGoogleCardClick(card) {
    this.openGoogleClassroomPostSidebar(card)
    this.onMouseLeave()
  }
}

// Give a type signature to the component.
// This needs to be after the `export default class` or else Vite gets confused.
type CardArgs = Signature["Args"]
export default interface CardComponent extends CardArgs {}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Planner::CardStacks::Cards::Card": typeof CardComponent
  }
}
